import { useCallback, useEffect, useState } from 'react'

import * as ScreenOrientation from 'expo-screen-orientation'
import { useContractsByTabNumberQuery } from 'integration/resources/wallet'
import { useBreakpointValue } from 'native-base'
import { PANELS_TYPES_ENUM } from 'organisms/TabPanels/constants'
import { useContractListScreenAtomValue } from 'src/store/screens/contractListScreen'
import { formatToReal, getMonthName } from 'src/utils'

import { UseContractListScreen } from './ContractListScreen.types'

const changeScreenOrientation = (orientation: ScreenOrientation.OrientationLock) => {
  return ScreenOrientation.lockAsync(orientation)
}

export const useContractListScreen: UseContractListScreen = ({ navigation, route }) => {
  const isMobile = useBreakpointValue({ base: true, lg: false })

  const [showSpinner, setShowSpinner] = useState(false)

  const contractListScreen = useContractListScreenAtomValue()

  const { tabNumber, yearMonth, title, description, type } = route.params

  const { data, isLoading } = useContractsByTabNumberQuery({ tabNumber, yearMonth })

  const contractList = data?.data.data

  const month = getMonthName(Number(yearMonth.slice(4)))

  const year = yearMonth.slice(0, 4)

  const handleGoBack = useCallback(() => {
    navigation.goBack()
  }, [navigation])

  useEffect(
    () =>
      navigation.addListener('beforeRemove', (e) => {
        if (e.data.action.type === 'GO_BACK') {
          setShowSpinner(true)

          if (
            navigation.getState().routes?.[navigation.getState().routes.length - 2]?.name ===
            'Chart'
          ) {
            changeScreenOrientation(ScreenOrientation.OrientationLock.LANDSCAPE_RIGHT)
          }
        }
      }),
    [navigation]
  )

  const getDescriptionSegment = (type: PANELS_TYPES_ENUM) => {
    if (contractList) {
      switch (type) {
        case PANELS_TYPES_ENUM.MARKETING_SHARE:
          return `Market Share ${month} ${year} (%)`

        case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
          return '% IP Seguro Prestamista (Qtd)'

        case PANELS_TYPES_ENUM.OVER30_M3:
          return `Over 30 M3 ${month} ${year} (R$)`

        case PANELS_TYPES_ENUM.FPD10:
          return `Contratos FPD10 ${month} ${year} (R$)`

        case PANELS_TYPES_ENUM.SANTANDER_AUTO_INSURANCE:
          return `Produção ${month} ${year} (R$)`
      }
    }
  }

  const getValueOfSegment = (type: PANELS_TYPES_ENUM) => {
    if (contractList) {
      switch (type) {
        case PANELS_TYPES_ENUM.MARKETING_SHARE:
          return `${contractList.market_share}%`

        case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
          return `${contractList?.ip_insurance_spf_qty}%`

        case PANELS_TYPES_ENUM.OVER30_M3:
          return `${formatToReal(contractList?.vl_over30_m3 ?? '0')}`

        case PANELS_TYPES_ENUM.FPD10:
          return `${formatToReal(contractList?.vl_fpd10 ?? '0')}`

        case PANELS_TYPES_ENUM.SANTANDER_AUTO_INSURANCE:
          return `${formatToReal(contractList?.production ?? '0')}`
      }
    }
  }

  const getValueOfContract = (type: PANELS_TYPES_ENUM) => {
    if (contractList) {
      switch (type) {
        case PANELS_TYPES_ENUM.PRODUCTION:
          return `${formatToReal(contractList?.production ?? '0')}`

        case PANELS_TYPES_ENUM.MARKETING_SHARE:
          return `${formatToReal(contractList?.production ?? '0')}`

        case PANELS_TYPES_ENUM.CREDIT_LIFE_INSURANCE:
          return `${contractList?.ip_insurance_spf_volume}%`

        case PANELS_TYPES_ENUM.OVER30_M3:
          return `${contractList?.over30_m3}%`

        case PANELS_TYPES_ENUM.FPD10:
          return `${contractList.fpd10}%`

        case PANELS_TYPES_ENUM.SANTANDER_AUTO_INSURANCE:
          return `${contractList?.santander_auto_insurance}%`
      }
    }
  }

  return {
    isMobile,
    isLoading,
    contractList,
    handleGoBack,
    showSpinner,
    title: isMobile ? title : contractListScreen?.title,
    tabNumber,
    description: isMobile ? description : contractListScreen?.description,
    valueOfContract: getValueOfContract(type),
    descriptionSegment: getDescriptionSegment(type),
    valueOfSegment: getValueOfSegment(type)
  }
}
